import React, { useState, useEffect, useCallback } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ppslogo from '../assets/ppslogo.png';
import './Dashboard.css';
import authService from '../services/authService';
import niraService from "../services/niraService";
import StanbicService from '../services/StanbicService';
import StanChartService from '../services/StanChartService';
import OauthClientService from '../services/OauthClientService';
import NotificationsService from '../services/NotificationsService';
import { useAlert } from '../context/AlertContext';
import NiraVerificationDialog from "../components/nira/NiraVerificationDialog";
import NiraResetPasswordDialog from "../components/nira/NiraResetPasswordDialog";
import DashboardSidebar from '../components/dashboard/DashboardSidebar';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import IntegrationTabs from '../components/dashboard/IntegrationTabs';
import ConfigForm from '../components/dashboard/ConfigForm';
import LogsView from '../components/dashboard/LogsView';
import RegisterView from '../components/dashboard/RegisterView';
import OauthClientsView from "../components/dashboard/OauthClientsView";
import StatsView from "../components/dashboard/StatsView";
import SmtpSetupView from '../components/notifications/SmtpSetupView';
import NotificationTemplatesView from '../components/notifications/NotificationTemplatesView';
import NiraTestResultsDialog from "../components/nira/NiraTestResultsDialog";

// Constants for configuration defaults
const DEFAULT_CONFIG = {
  nira: {
    url: '',             // e.g. https://api.nita.go.ug
    tokenBaseUrl: '',
    tokenExpiry: '',
    username: '',
    password: '',
    passwordDaysLeft: '',
    consumerKey: '',
    secretKey: '',
    authConsumerKey: '',
    authSecretKey: '',
    dateCreated: '',
    dateUpdated: '',
    enableSync: false,
    syncFrequency: 'daily',
  },
  stanbic: {
    id: 1,
    schemeCode: 'PPS',
    customerId: 'CUST12345',
    userId: 'PPS',
    instructionType: 'CREDIT_TRANSFER',
    integrationFolder: '/integrations/stanbic',
    filesOutboxFolder: '/outbox',
    filesInboxFolder: '/inbox',
    filesInboxBackupFolder: '/inbox/backup',
    filesOutboxFolderEncrypted: '/outbox/encrypted',
    filesInboxFolderEncrypted: '/inbox/encrypted',
    enableEncryption: 'YES',
    bankPgpKeyEmail: '',
    bankPgpKeyPath: '',
    localPgpKeyCompanyName: '',
    localPgpKeyEmail: '',
    localPgpKeyPassword: '',
    sftpUsername: 'pps_user',
    sftpRemoteHost: 'sftp.stanbicbank.co.ug',
    sftpRemotePort: 22,
    sftpKnownHostsPath: '',
    sftpRemoteOutboxPath: '/remote/outbox',
    sftpRemoteInboxPath: '/remote/inbox',
    sftpPrivateKeyPath: '',
    privateKeyPassword: '********',
    activateSftp: 'YES',
    activateIntegration: 'YES',
    combineUserAndCustomerOnFileName: 'NO',
    promoteToProduction: 'NO',
    replaceSpecialChars: 'YES',
    roundPaymentsTo: 'NEAREST_FIFTY_CENTS',
    truncateTrailingDecimals: 'NO',
    sshServerAddress: '',
    sshServerUsername: '',
    sshServerPassword: '',
    generateSshKeyPassword: '',
    fileNamePrefix: 'PPS',
  },
  stanchart: {
    id: '',
    // API Configuration
    apiUrl: 'https://openbanking.sc.com',
    paymentInitiationEndPoint: '/api/cui/l3-cms/uat2/openapi/payments/v2/initiate',
    paymentStatusEndPoint: '/api/cui/l3-cms/uat2/openapi/payments/v2/status',

    // Service Activation
    activationUrl: 'https://apitest.standardchartered.com/uat2/activate',
    activationKey: '',

    // Webhook Configuration
    webhookUrl: '',
    enableWebhook: 'NO',

    // Security Configuration
    jwtIssuer: 'SCB',
    jwtAudience: 'SCB-APIBanking',
    jwtExpiryMinutes: 5,

    // System Configuration
    enableIntegration: 'YES',
    promoteToProduction: 'NO',
    jwtTimeoutSeconds: 30000,

    // Additional required fields
    apiKey: '',
    environment: 'sandbox',
    basePath: '/v1',
    certificatePath: '',
    privateKeyPath: '',
    passphrase: ''
  }
};

const DEFAULT_FILTERS = {
  dateFrom: '',
  dateTo: '',
  searchTerm: '',
  status: '',
  cashbookCode: ''
};

const Dashboard = () => {
  // State management
  const [activeTab, setActiveTab] = useState('dashboard');
  const [activeSubTab, setActiveSubTab] = useState('register');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userData, setUserData] = useState(null);
  const [isVerifyingSession, setIsVerifyingSession] = useState(true);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [isGeneratingStanbicSSHKey, setIsGeneratingStanbicSSHKey] = useState(false);
  const [isGeneratingStanbicPGPKey, setIsGeneratingStanbicPGPKey] = useState(false);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [stanbicLogs, setStanbicLogs] = useState([]);
  const [niraLogs, setNiraLogs] = useState([]);
  const [stanchartLogs, setStanchartLogs] = useState([]);
  const [expandedLogs, setExpandedLogs] = useState({});

  // Stanbic-specific state
  const [stanbicData, setStanbicData] = useState([]);
  const [stanbicLoading, setStanbicLoading] = useState(false);
  const [stanbicError, setStanbicError] = useState(null);
  const [stanbicBreakdownData, setStanbicBreakdownData] = useState([]);
  const [stanbicBreakdownLoading, setStanbicBreakdownLoading] = useState(false);
  const [stanbicPaymentResponses, setStanbicPaymentResponses] = useState([]);
  const [selectedStanbicBatchId, setSelectedStanbicBatchId] = useState(null);

  // StanChart-specific state
  const [isTestingStanchartConnection, setIsTestingStanchartConnection] = useState(false);
  const [isActivatingStanchartService, setIsActivatingStanchartService] = useState(false);

  // Batch functionality state
  const [batchData, setBatchData] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchError, setBatchError] = useState(null);
  const [batchBreakdownData, setBatchBreakdownData] = useState([]);
  const [batchBreakdownLoading, setBatchBreakdownLoading] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState(null);

  // NIRA-specific state
  const [niraPage, setNiraPage] = useState({
    content: [],
    totalElements: 0,
    totalPages: 0,
    number: 0,
    size: 10,
    first: true,
    last: true,
    empty: true,
  });
  const [niraLoading, setNiraLoading] = useState(false);
  const [niraError, setNiraError] = useState(null);
  const [niraSize, setNiraSize] = useState(10);

  // Notifications state
  const [notificationPlatforms, setNotificationPlatforms] = useState([]);
  const [notificationCategories, setNotificationCategories] = useState([]);
  const [notificationTemplateApplications, setNotificationTemplateApplications] = useState([]);
  const [notificationTypes, setNotificationTypes] = useState([]);
  const [reservedWords, setReservedWords] = useState([]);
  const [smtpSetups, setSmtpSetups] = useState([]);
  const [isLoadingNotificationData, setIsLoadingNotificationData] = useState(false);

  // Other states
  const [isTestingNiraIntegration, setIsTestingNiraIntegration] = useState(false);
  const [isResettingNiraPassword, setIsResettingNiraPassword] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [niraStatusCounts, setNiraStatusCounts] = useState([]);
  const [niraTrendsByMonth, setNiraTrendsByMonth] = useState([]);
  const [oauthClients, setOAuthClients] = useState([]);
  const [isLoadingOAuthClients, setIsLoadingOAuthClients] = useState(false);
  const [isCreatingOAuthClient, setIsCreatingOAuthClient] = useState(false);
  const [isRegeneratingOAuthClient, setIsRegeneratingOAuthClient] = useState(false);
  const [isDeletingOAuthClient, setIsDeletingOAuthClient] = useState(false);

  // Configuration data
  const [configData, setConfigData] = useState(DEFAULT_CONFIG);

  const { showAlert } = useAlert();

  // Authentication and initialization
  useEffect(() => {
    const checkAuth = async () => {
      try {
        setIsVerifyingSession(true);

        const token = authService.getAccessToken();
        if (!token) {
          authService.forceLogout();
          return;
        }

        if (authService.isTokenExpired()) {
          const newToken = await authService.getValidAccessToken();
          if (!newToken) {
            authService.forceLogout();
            return;
          }
        }

        const user = authService.getUserData();
        if (!user) {
          authService.forceLogout();
          return;
        }

        setUserData(user);
      } catch (error) {
        console.error('Auth check failed:', error);
        authService.forceLogout();
      } finally {
        setIsVerifyingSession(false);
      }
    };

    checkAuth();
  }, []);

  // Load notification enums
  useEffect(() => {
    if (activeTab === 'smtp-setup' || activeTab === 'notification-templates') {
      loadNotificationEnums();
    }
  }, [activeTab]);

  const loadNotificationEnums = async () => {
    try {
      setIsLoadingNotificationData(true);
      const [platformsRes, categoriesRes, appsRes, typesRes, reservedWordsRes, smtpSetupsRes] = await Promise.all([
        NotificationsService.getNotificationPlatforms(),
        NotificationsService.getNotificationCategories(),
        NotificationsService.getNotificationTemplateApplications(),
        NotificationsService.getNotificationTypes(),
        NotificationsService.getReservedWords(),
        NotificationsService.getSmtpSetups()
      ]);

      if (platformsRes.success) setNotificationPlatforms(platformsRes.data || []);
      if (categoriesRes.success) setNotificationCategories(categoriesRes.data || []);
      if (appsRes.success) setNotificationTemplateApplications(appsRes.data || []);
      if (typesRes.success) setNotificationTypes(typesRes.data || []);

      // Handle reserved words safely
      if (reservedWordsRes.success) {
        const reservedWordsData = reservedWordsRes.data || [];
        console.log('Raw reserved words:', reservedWordsData);
        setReservedWords(reservedWordsData);
      }

      if (smtpSetupsRes.success) {
        // Set the raw SMTP setups data - NotificationTemplatesView will handle it
        console.log('SMTP setups loaded:', smtpSetupsRes.data);
        setSmtpSetups(smtpSetupsRes.data || []);
      }
    } catch (error) {
      console.error('Failed to load notification enums:', error);
      showAlert('Failed to load notification data', 'error');
    } finally {
      setIsLoadingNotificationData(false);
    }
  };

  // Load configuration when tab changes
  useEffect(() => {
    if (activeTab === 'stanbic' && activeSubTab === 'config') {
      loadStanbicConfig().catch(e => console.error("Error loading Stanbic config:", e));
    }
    if (activeTab === 'stanbic' && activeSubTab === 'logs') {
      loadStanbicLogs().catch(e => console.error("Error loading Stanbic logs:", e));
    }
    if (activeTab === 'nira' && activeSubTab === 'config') {
      loadNiraConfig().catch(e => console.error('Error loading NIRA config:', e));
    }
    if (activeTab === 'nira' && activeSubTab === 'logs') {
      loadNiraLogs().catch(e => console.error('Error loading NIRA logs:', e));
    }
    if (activeTab === 'nira' && activeSubTab === 'stats') {
      loadNiraStats({rangeType: 'LAST_1_MONTH'}).catch(e => console.error('Error loading NIRA logs:', e));
    }
    if (activeTab === 'stanchart' && activeSubTab === 'config') {
      loadStanchartConfig().catch(e => console.error('Error loading StanChart config:', e));
    }
    if (activeTab === 'stanchart' && activeSubTab === 'logs') {
      loadStanchartLogs().catch(e => console.error('Error loading StanChart logs:', e));
    }
  }, [activeTab, activeSubTab]);

  // Load OAuth clients when tab is active
  useEffect(() => {
    if (activeTab === 'oauth-clients') {
      loadOAuthClients();
    }
  }, [activeTab]);

  // Load data when tab is active
  useEffect(() => {
    if (activeTab === 'nira' && activeSubTab === 'register') {
      loadNiraPage(niraPage.number, niraSize).catch(e => console.error("Error loading Nira config:", e));
    } else if (activeTab === 'stanbic' && activeSubTab === 'register') {
      fetchStanbicBatchList(filters);
    } else if (activeTab === 'batch' && activeSubTab === 'register') {
      fetchBatchList(filters);
    } else if (activeTab === 'dashboard') {
      // Dashboard overview
    }
  }, [activeTab, activeSubTab]);

  // Stanbic batch functions
  const fetchStanbicBatchList = async (filters) => {
    setStanbicLoading(true);
    setStanbicError(null);
    try {
      const response = await StanbicService.getStanbicBatchPayments(filters);

      if (response.success) {
        setStanbicData(response.data || []);
        showAlert('Stanbic batches loaded successfully', 'success');
      } else {
        setStanbicError(response.message || 'Failed to load Stanbic batches');
        showAlert(response.message || 'Failed to load Stanbic batches', 'error');
      }
    } catch (error) {
      console.error('Error fetching Stanbic batches:', error);
      setStanbicError(error.message || 'Failed to fetch Stanbic batches');
      showAlert('Failed to fetch Stanbic batches', 'error');
    } finally {
      setStanbicLoading(false);
    }
  };

  const fetchStanbicBatchBreakdown = async (batchId) => {
    setStanbicBreakdownLoading(true);
    try {
      // Fetch batch lines
      const linesResponse = await StanbicService.getStanbicBatchBreakdown({ batchId });
      if (linesResponse.success) {
        setStanbicBreakdownData(linesResponse.data || []);
      }

      // Fetch payment responses
      const responsesResponse = await fetch(`/api/v1/s2b/payment-responses/${batchId}`);
      if (responsesResponse.ok) {
        const data = await responsesResponse.json();
        if (data.success) {
          setStanbicPaymentResponses(data.data || []);
        }
      }
    } catch (error) {
      console.error('Error fetching Stanbic breakdown:', error);
      setStanbicError(error.message || 'Failed to fetch batch breakdown');
    } finally {
      setStanbicBreakdownLoading(false);
    }
  };

  const handleViewStanbicBatch = (batchId) => {
    setSelectedStanbicBatchId(batchId);
    fetchStanbicBatchBreakdown(batchId);
  };

  const handleCloseStanbicBreakdown = () => {
    setSelectedStanbicBatchId(null);
    setStanbicBreakdownData([]);
    setStanbicPaymentResponses([]);
  };

  // Batch functions
  const fetchBatchList = async (filters) => {
    setBatchLoading(true);
    setBatchError(null);
    try {
      // Mock data for demonstration
      const mockBatchData = [
        {
          id: 1,
          batchTitle: 'Salary Payment Batch 1',
          schemeName: 'Salary Scheme',
          batchDate: '2024-01-15',
          valueDate: '2024-01-16',
          countLines: 150,
          totalBaseCurrency: 45000000,
          preparedByName: 'John Doe',
          cashbookName: 'Main Cashbook'
        }
      ];
      setBatchData(mockBatchData);
    } catch (error) {
      setBatchError(error.response?.data?.message || 'Failed to fetch batch data');
    } finally {
      setBatchLoading(false);
    }
  };

  const fetchBatchBreakdown = async (batchId) => {
    setBatchBreakdownLoading(true);
    try {
      // Mock data for demonstration
      const mockBreakdownData = [
        {
          paymentId: 1001,
          customerRef: 'CUST001',
          beneficiaryName: 'Alice Johnson',
          accountNo: '1234567890',
          bankName: 'Stanbic Bank',
          amountSc: 300000,
          amountBc: 300000,
          paymentCurrencyCode: 'UGX',
          purposeOfPayment: 'Salary Payment'
        }
      ];
      setBatchBreakdownData(mockBreakdownData);
    } catch (error) {
      setBatchError(error.response?.data?.message || 'Failed to fetch batch breakdown');
    } finally {
      setBatchBreakdownLoading(false);
    }
  };

  const handleViewBatchBreakdown = (batchId) => {
    setSelectedBatchId(batchId);
    fetchBatchBreakdown(batchId);
  };

  const handleCloseBreakdown = () => {
    setSelectedBatchId(null);
    setBatchBreakdownData([]);
  };

  // StanChart Configuration functions
  const loadStanchartConfig = useCallback(async () => {
    try {
      setIsLoadingConfig(true);
      const response = await StanChartService.getConfiguration();

      if (response.success && response.data) {
        setConfigData(prev => ({
          ...prev,
          stanchart: {
            ...DEFAULT_CONFIG.stanchart, // Start with defaults
            ...response.data // Override with API data
          }
        }));
        showAlert('StanChart configuration loaded successfully!', 'success');
      } else {
        showAlert('Failed to load StanChart configuration: '+response.message, 'error');
      }
    } catch (error) {
      console.error('Failed to load StanChart configuration:', error);
      showAlert('Failed to load StanChart configuration', 'error');
    } finally {
      setIsLoadingConfig(false);
    }
  }, [showAlert]);

  const saveStanchartConfig = async () => {
    try {
      setIsSavingConfig(true);

      // Clean the config data before sending
      const cleanConfig = cleanStanchartConfig(configData.stanchart);

      const response = await StanChartService.saveConfiguration(cleanConfig);

      if (response.success) {
        showAlert('StanChart configuration saved successfully!', 'success');
      } else {
        showAlert('Failed to save StanChart configuration: ' + (response.message || 'Unknown error'), 'error');
      }
    } catch (error) {
      console.error('Failed to save StanChart configuration:', error);
      showAlert('Failed to save StanChart configuration: ' + error.message, 'error');
    } finally {
      setIsSavingConfig(false);
    }
  };

  // Helper function to clean StanChart config
  const cleanStanchartConfig = (config) => {

    const cleaned = { ...config };

    // Remove empty strings and replace with null for required fields
    Object.keys(cleaned).forEach(key => {
      if (cleaned[key] === '') {
        cleaned[key] = null;
      }
    });

    // Ensure all required fields are present
    return {
      ...DEFAULT_CONFIG.stanchart,
      ...cleaned
    };

  };

  const handleTestStanchartConnection = async () => {
    try {
      setIsTestingStanchartConnection(true);

      if (!configData.stanchart.apiUrl) {
        showAlert('Please fill in API URL', 'error');
        return;
      }

      confirmAlert({
        title: 'Test StanChart Connection',
        message: 'Are you sure you want to test the connection to Standard Chartered Bank API?',
        buttons: [
          {
            label: 'Yes',
            onClick: async () => {
              try {
                const cleanConfig = cleanStanchartConfig(configData.stanchart);
                const response = await StanChartService.testConnection(cleanConfig);
                if (response.success) {
                  showAlert('StanChart API connection test successful!', 'success');
                } else {
                  showAlert('StanChart API connection test failed: ' + (response.message || 'Unknown error'), 'error');
                }
              } catch (error) {
                showAlert('StanChart API connection test failed: ' + error.message, 'error');
              }
            }
          },
          {
            label: 'No',
            onClick: () => {}
          }
        ]
      });
    } catch (error) {
      showAlert('Connection test failed: ' + error.message, 'error');
    } finally {
      setIsTestingStanchartConnection(false);
    }
  };

  const handleActivateStanchartService = async () => {
    try {
      setIsActivatingStanchartService(true);

      if (!configData.stanchart.activationKey) {
        showAlert('Please provide the activation key received from Standard Chartered Bank', 'error');
        return;
      }

      confirmAlert({
        title: 'Activate StanChart Service',
        message: 'This will activate your service with Standard Chartered Bank. This operation can only be performed once. Are you sure you want to proceed?',
        buttons: [
          {
            label: 'Yes, Activate',
            onClick: async () => {
              try {
                const cleanConfig = cleanStanchartConfig(configData.stanchart);
                const response = await StanChartService.activate(cleanConfig);
                if (response.success) {
                  showAlert('StanChart service activated successfully! You can now use the core APIs.', 'success');
                } else {
                  showAlert('StanChart service activation failed: ' + (response.message || 'Unknown error'), 'error');
                }
              } catch (error) {
                showAlert('StanChart service activation failed: ' + error.message, 'error');
              }
            }
          },
          {
            label: 'Cancel',
            onClick: () => {}
          }
        ]
      });
    } catch (error) {
      showAlert('Service activation failed: ' + error.message, 'error');
    } finally {
      setIsActivatingStanchartService(false);
    }
  };

  // OAuth Clients functions
  const loadOAuthClients = async () => {
    try {
      setIsLoadingOAuthClients(true);
      const response = await OauthClientService.getClients();

      if (response.success) {
        setOAuthClients(response.data || []);
      } else {
        showAlert(response.message || 'Failed to load OAuth clients', 'error');
      }
    } catch (error) {
      console.error('Failed to load OAuth clients:', error);
      showAlert('Failed to load OAuth clients', 'error');
    } finally {
      setIsLoadingOAuthClients(false);
    }
  };

  const handleCreateOAuthClient = async (clientName) => {
    try {
      setIsCreatingOAuthClient(true);
      const response = await OauthClientService.saveClient({ clientName });

      if (response.success) {
        showAlert('Client created successfully!', 'success');
        loadOAuthClients();
      } else {
        showAlert(response.message || 'Failed to create OAuth client', 'error');
      }
    } catch (error) {
      console.error('Failed to create OAuth client:', error);
      showAlert('Failed to create OAuth client', 'error');
    } finally {
      setIsCreatingOAuthClient(false);
    }
  };

  const handleRegenerateOAuthCredentials = async (clientId) => {
    try {
      setIsRegeneratingOAuthClient(true);
      const response = await OauthClientService.regenerateCredentials(clientId);

      if (response.success) {
        showAlert(response.message, 'success');
        loadOAuthClients();
      } else {
        showAlert(response.message || 'Failed to regenerate credentials', 'error');
      }
    } catch (error) {
      console.error('Failed to regenerate credentials:', error);
      showAlert('Failed to regenerate credentials', 'error');
    } finally {
      setIsRegeneratingOAuthClient(false);
    }
  };

  const handleDeleteOAuthClient = async (clientId) => {
    try {
      setIsDeletingOAuthClient(true);
      const response = await OauthClientService.deleteClient(clientId);

      if (response.success) {
        showAlert(response.message, 'success');
        loadOAuthClients();
      } else {
        showAlert(response.message || 'Failed to delete OAuth client', 'error');
      }
    } catch (error) {
      console.error('Failed to delete OAuth client:', error);
      showAlert('Failed to delete OAuth client', 'error');
    } finally {
      setIsDeletingOAuthClient(false);
    }
  };

  // Configuration loading functions
  const loadStanbicConfig = useCallback(async () => {
    try {
      setIsLoadingConfig(true);
      const response = await StanbicService.getConfiguration();

      if (response.success && response.data) {
        setConfigData(prev => ({
          ...prev,
          stanbic: {
            ...prev.stanbic,
            ...response.data
          }
        }));
        showAlert('Stanbic configuration loaded successfully!', 'success');
      } else {
        showAlert('Failed to load Stanbic configuration', 'error');
      }
    } catch (error) {
      console.error('Failed to load Stanbic configuration:', error);
      showAlert('Failed to load Stanbic configuration', 'error');
    } finally {
      setIsLoadingConfig(false);
    }
  }, [showAlert]);

  const loadNiraConfig = useCallback(async () => {
    try {
      setIsLoadingConfig(true);
      const resp = await niraService.fetchNiraConfiguration();
      const data = resp?.data ?? resp;
      if (data) {
        setConfigData(prev => ({
          ...prev,
          nira: {
            ...prev.nira,
            ...data,
          }
        }));
      }
    } catch (e) {
      console.error('Failed to load NIRA configuration:', e);
      toast.error(e?.response?.data?.message || e?.message || 'Failed to load NIRA configuration', {
        position: 'top-center',
        autoClose: 3000
      });
    } finally {
      setIsLoadingConfig(false);
    }
  }, []);

  // Logs management
  const loadStanbicLogs = async () => {
    try {
      setIsLoadingLogs(true);
      const logFilters = {
        dateFrom: filters.dateFrom || '',
        dateTo: filters.dateTo || '',
        searchKey: filters.searchTerm || ''
      };
      const response = await StanbicService.getAuditTrails(logFilters);
      if (response.success && response.data) {
        setStanbicLogs(response.data);
        setExpandedLogs({});
        showAlert('Stanbic logs loaded successfully!', 'success');
      } else {
        showAlert(response.message || 'Failed to load Stanbic logs', 'error');
      }
    } catch (error) {
      console.error('Failed to load Stanbic logs:', error);
      showAlert(error.message || 'Failed to load Stanbic logs', 'error');
    } finally {
      setIsLoadingLogs(false);
    }
  };

  const loadNiraLogs = async () => {
    try {
      setIsLoadingLogs(true);
      const logFilters = {
        dateFrom: filters.dateFrom || '',
        dateTo: filters.dateTo || '',
        searchKey: filters.searchTerm || ''
      };
      const response = await niraService.fetchAuditTrails(logFilters);
      if (response.success && response.data) {
        setNiraLogs(response.data);
        setExpandedLogs({});
        showAlert('Nira logs loaded successfully!', 'success');
      } else {
        showAlert(response.message || 'Failed to load Nira logs', 'error');
      }
    } catch (error) {
      console.error('Failed to load Nira logs:', error);
      showAlert(error.message || 'Failed to load Nira logs', 'error');
    } finally {
      setIsLoadingLogs(false);
    }
  };

  const handleLoadStats = (filterData) => {
    if (activeTab === 'nira') {
      loadNiraStats(filterData);
    }
  };

  const loadNiraStats = async (filterData) => {
    try {
      setIsLoadingStats(true);

      const response = await niraService.fetchNiraStatistics(filterData);

      if (response.success) {
        setNiraStatusCounts(response.statusCounts || []);
        setNiraTrendsByMonth(response.trendsByMonth || []);
      } else {
        showAlert('Failed to load statistics', 'error');
        setNiraStatusCounts([]);
        setNiraTrendsByMonth([]);
      }
    } catch (error) {
      console.error('Failed to load NIRA statistics:', error);
      showAlert('Failed to load statistics', 'error');
      setNiraStatusCounts([]);
      setNiraTrendsByMonth([]);
    } finally {
      setIsLoadingStats(false);
    }
  };

  const loadStanchartLogs = async () => {
    try {
      setIsLoadingLogs(true);
      const logFilters = {
        dateFrom: filters.dateFrom || '',
        dateTo: filters.dateTo || '',
        searchKey: filters.searchTerm || ''
      };
      const response = await StanChartService.getAuditTrails(logFilters);
      if (response.success && response.data) {
        setStanchartLogs(response.data);
        setExpandedLogs({});
        showAlert('StanChart logs loaded successfully!', 'success');
      } else {
        showAlert(response.message || 'Failed to load StanChart logs', 'error');
      }
    } catch (error) {
      console.error('Failed to load StanChart logs:', error);
      showAlert(error.message || 'Failed to load StanChart logs', 'error');
    } finally {
      setIsLoadingLogs(false);
    }
  };

  const toggleLogExpansion = (logId) => {
    setExpandedLogs(prev => ({
      ...prev,
      [logId]: !prev[logId]
    }));
  };

  // NIRA data management
  const loadNiraPage = useCallback(async (pageNum = 0, pageSize = niraSize, query = undefined) => {
    try {
      setNiraLoading(true);
      setNiraError(null);
      const q = query ?? buildNiraQuery(filters);
      const page = await niraService.fetchNiraVerificationRequestsPage({
        page: pageNum,
        size: pageSize,
        query: q
      });
      setNiraPage(page);
    } catch (e) {
      console.error('Failed to fetch NIRA verification requests:', e);
      setNiraError(e?.message || 'Failed to load data');
      setNiraPage(prev => ({
        ...prev,
        content: [],
        totalElements: 0,
        totalPages: 0,
        empty: true
      }));
    } finally {
      setNiraLoading(false);
    }
  }, [filters, niraSize]);

  // Configuration saving functions
  const saveStanbicConfig = async () => {
    try {
      setIsSavingConfig(true);
      const response = await StanbicService.saveConfiguration(configData.stanbic);

      if (response.success) {
        showAlert(response.message, 'success');
      } else {
        showAlert('Failed to save Stanbic configuration: ' + (response.message || 'Unknown error'), 'error');
      }
    } catch (error) {
      console.error('Failed to save Stanbic configuration:', error);
      showAlert('Failed to save Stanbic configuration. Please try again.', 'error');
    } finally {
      setIsSavingConfig(false);
    }
  };

  const saveNiraConfig = async () => {
    try {
      setIsSavingConfig(true);
      const payload = configData.nira;
      const resp = await niraService.postIntegrationConfig('nira', payload);
      const success = resp?.success === true;
      const message = resp?.message || (success ? 'Configuration saved successfully' : 'Save Configuration failed');
      toast.success(message, {
        position: "top-center",
        className: "toast-success",
        autoClose: 2000,
      });
    } catch (error) {
      console.error('Error posting NIRA config:', error);
      const msg = error?.response?.data?.message || error?.message || 'Unknown error';
      toast.error(`Failed to save NIRA configuration: ${msg}`, {
        position: "top-center",
        autoClose: 5000,
      });
    } finally {
      setIsSavingConfig(false);
    }
  };

  const saveConfig = async (integration) => {
    if (integration === 'stanbic') {
      await saveStanbicConfig();
    } else if (integration === 'nira') {
      await saveNiraConfig();
    } else if (integration === 'stanchart') {
      await saveStanchartConfig();
    } else {
      console.log(`Saving ${integration} config:`, configData[integration]);
      showAlert(`${integration.toUpperCase()} configuration saved successfully!`, 'success');
    }
  };

  const reloadConfig = async (integration) => {
    if (integration === 'stanbic') {
      await loadStanbicConfig();
    } else if (integration === 'nira') {
      await loadNiraConfig();
    } else if (integration === 'stanchart') {
      await loadStanchartConfig();
    } else {
      console.log(`Reloading ${integration} config:`, configData[integration]);
    }
  };

  // Key generation functions
  const handleGenerateSSHKey = async () => {
    if (configData.stanbic.sftpKnownHostsPath) {
      confirmAlert({
        title: 'Confirm SSH Key Regeneration',
        message: 'An SSH key already exists. Are you sure you want to regenerate it? This may require updating configurations on the remote server.',
        buttons: [
          {
            label: 'Yes',
            onClick: () => generateSSHKeysForStanbic()
          },
          {
            label: 'No',
            onClick: () => {}
          }
        ]
      });
    } else {
      generateSSHKeysForStanbic();
    }
  };

  const generateSSHKeysForStanbic = async () => {
    try {
      setIsGeneratingStanbicSSHKey(true);
      const response = await StanbicService.generateSSHKey(configData.stanbic);

      if (response.success) {
        showAlert(response.message, 'success');
        loadStanbicConfig();
      } else {
        showAlert('Failed to Generate SSH Keys: ' + (response.message || 'Unknown error'), 'error');
      }
    } catch (error) {
      showAlert('Failed to Generate SSH Keys for Stanbic integration. Please try again. '+error, 'error');
    } finally {
      setIsGeneratingStanbicSSHKey(false);
    }
  };

  const handleGeneratePGPKey = async () => {
    if (!configData.stanbic.localPgpKeyCompanyName ||
        !configData.stanbic.localPgpKeyEmail ||
        !configData.stanbic.localPgpKeyPassword) {
      showAlert('Please fill in all required PGP key fields (Company Name, Email, and Password)', 'error');
      return;
    }

    // First silly confirmation
    confirmAlert({
      title: '🚨 DANGER ZONE 🚨',
      message: 'Are you absolutely, positively, 100% sure you want to do this? PGP keys are serious business!',
      buttons: [
        {
          label: 'I live dangerously!',
          onClick: () => {
            // Second silly confirmation
            confirmAlert({
              title: '🤔 ARE YOU REALLY SURE? 🤔',
              message: 'Like, for real for real? This isn\'t a drill!',
              buttons: [
                {
                  label: 'Yes, I know what I\'m doing!',
                  onClick: () => {
                    // Third silly confirmation
                    confirmAlert({
                      title: 'LAST CHANCE TO BACK OUT!',
                      message: 'Final warning: This will create cryptographic magic. No take-backsies!',
                      buttons: [
                        {
                          label: 'I ACCEPT THE CONSEQUENCES!',
                          onClick: () => {
                            // Passphrase request
                            const passphrase = prompt('To prove you\'re not a fool, enter the secret passphrase:\n\n"I AM NOT A FOOL TO BE ASKED THIS"');

                            if (passphrase === "I AM NOT A FOOL TO BE ASKED THIS") {
                              if (configData.stanbic.localPgpKeyEmail) {
                                confirmAlert({
                                  title: 'Confirm PGP Key Regeneration',
                                  message: 'Are you sure you want to generate a NEW PGP KEY? This may require updating configurations at the Stanbic Servers.',
                                  buttons: [
                                    {
                                      label: 'Yes',
                                      onClick: () => generatePGPKeysForStanbic()
                                    },
                                    {
                                      label: 'No',
                                      onClick: () => {}
                                    }
                                  ]
                                });
                              } else {
                                generatePGPKeysForStanbic();
                              }
                            } else {
                              showAlert('Incorrect passphrase! Maybe you are a fool after all...', 'error');
                            }
                          }
                        },
                        {
                          label: 'Okay, I got scared',
                          onClick: () => {}
                        }
                      ]
                    });
                  }
                },
                {
                  label: 'Actually, I\'m having doubts',
                  onClick: () => {}
                }
              ]
            });
          }
        },
        {
          label: 'Nevermind, too scary!',
          onClick: () => {}
        }
      ]
    });
  };

  const generatePGPKeysForStanbic = async () => {
    try {
      setIsGeneratingStanbicPGPKey(true);
      const response = await StanbicService.generatePGPKey(configData.stanbic);

      if (response.success) {
        showAlert(response.message, 'success');
        loadStanbicConfig();
      } else {
        showAlert('Failed to Generate PGP Keys: ' + (response.message || 'Unknown error'), 'error');
      }
    } catch (error) {
      showAlert('Failed to Generate PGP Keys for Stanbic integration. Please try again. '+error, 'error');
    } finally {
      setIsGeneratingStanbicPGPKey(false);
    }
  };

  // Connection testing
  const handleTestConnection = async () => {
    if (!configData.stanbic.sftpRemoteHost ||
        !configData.stanbic.sftpUsername ||
        !configData.stanbic.sshServerAddress) {
      showAlert('Please fill in all required connection fields (Remote Host, Username, and SSH Server Address)', 'error');
      return;
    }

    confirmAlert({
      title: 'Test Connection',
      message: 'Are you sure you want to test the connection to the SFTP server?',
      buttons: [
        {
          label: 'Yes',
          onClick: () => testConnection()
        },
        {
          label: 'No',
          onClick: () => {}
        }
      ]
    });
  };

  const testConnection = async () => {
    try {
      setIsTestingConnection(true);
      const response = await StanbicService.testConnection(configData.stanbic);

      if (response.success) {
        showAlert(response.message, 'success');
      } else {
        showAlert('Connection Test Failed: ' + (response.message || 'Unknown error'), 'error');
      }
    } catch (error) {
      showAlert('Connection Test Failed. Please try again. '+error, 'error');
    } finally {
      setIsTestingConnection(false);
    }
  };

  // NIRA integration testing
  const handleTestNiraIntegration = () => {
    confirmAlert({
      customUI: ({ onClose }) => {
        // We use a wrapper component to handle the state of the test
        const TestRunner = () => {
          const [isLoading, setIsLoading] = React.useState(true);
          const [results, setResults] = React.useState([]);

          React.useEffect(() => {
            const runTests = async () => {
              try {
                // The hardcoded payload for the test suite
                const payload = {
                  nationalId: "CM930121003EGE",
                  dateOfBirth: "01/01/1993",
                  documentId: "000092564",
                  givenNames: "Johnson",
                  surname: "Tipiyai"
                };

                // Call the new endpoint
                const resp = await niraService.runAllIntegrationTests(payload);
                if (resp.success) {
                  setResults(resp.data);
                } else {
                  toast.error(resp.message || "Tests failed to run");
                }
              } catch (e) {
                toast.error(e?.response?.data?.message || e?.message || "Failed to run tests");
              } finally {
                setIsLoading(false);
              }
            };

            runTests();
          }, []);

          return (
              <NiraTestResultsDialog
                  onClose={onClose}
                  results={results}
                  isLoading={isLoading}
              />
          );
        };

        return <TestRunner />;
      }
    });
  };

  const handleResetNiraPassword = () => {
    confirmAlert({
      customUI: ({ onClose }) => (
          <NiraResetPasswordDialog
              onClose={onClose}
              onSubmit={async (payload) => {
                try {
                  setIsResettingNiraPassword(true);
                  const resp = await niraService.resetNiraPassword(payload);
                  const success = resp?.success;
                  const message = resp?.message || (success ? 'Password reset successfully' : 'Password reset failed');

                  if (success === false) {
                    toast.error(message, { position: 'top-center', autoClose: 4000 });
                  } else {
                    toast.success(message, { position: 'top-center', autoClose: 2500 });
                    onClose();
                  }
                } catch (e) {
                  const msg = e?.response?.data?.message || e?.message || 'Password reset failed';
                  toast.error(msg, { position: 'top-center', autoClose: 3500 });
                } finally {
                  setIsResettingNiraPassword(false);
                }
              }}
          />
      )
    });
  };

  const handleViewNiraRequest = async (id) => {
    if (!id) return;

    try {
      toast.info('Loading verification details...', { autoClose: 1200, hideProgressBar: true });
      const resp = await niraService.fetchNiraVerificationRequest(id);
      const payload = resp?.data ?? resp;

      if (!payload) {
        toast.error('Verification details not found');
        return;
      }

      confirmAlert({
        customUI: ({ onClose }) => (
            <NiraVerificationDialog
                onClose={onClose}
                initialValues={payload}
                hideActions={true}
                readOnly={true}
                title={`Verification #${payload.id ?? id}`}
            />
        )
      });
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to load verification details';
      toast.error(msg, { position: 'top-center', autoClose: 3000 });
    }
  };

  // Filtering utilities
  const buildNiraQuery = (flt) => {
    const q = {};
    const { dateFrom, dateTo, searchTerm, status } = flt || {};

    if (dateFrom) q.dateFrom = dateFrom;
    if (dateTo) q.dateTo = dateTo;
    if (searchTerm) q.searchTerm = searchTerm;
    if (status) q.status = status;

    return q;
  };

  const applyFilters = () => {
    if (activeTab === 'nira') {
      const query = buildNiraQuery(filters);
      loadNiraPage(0, niraSize, query);
    } else if (activeTab === 'stanbic') {
      fetchStanbicBatchList(filters);
    } else if (activeTab === 'batch') {
      fetchBatchList(filters);
    }
  };

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);

    if (activeTab === 'nira') {
      loadNiraPage(0, niraSize, buildNiraQuery(DEFAULT_FILTERS));
    } else if (activeTab === 'stanbic') {
      fetchStanbicBatchList(DEFAULT_FILTERS);
    } else if (activeTab === 'batch') {
      fetchBatchList(DEFAULT_FILTERS);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleConfigChange = (integration, field, value) => {
    setConfigData(prev => ({
      ...prev,
      [integration]: {
        ...prev[integration],
        [field]: value
      }
    }));
  };

  const onReloadLogs = () => {
    setFilters(DEFAULT_FILTERS);

    if (activeTab === 'nira') {
      loadNiraLogs().catch(e => console.error("Error reloading NIRA logs:", e));
    } else if (activeTab === 'stanbic') {
      loadStanbicLogs().catch(e => console.error("Error reloading stanbic logs:", e));
    } else if (activeTab === 'stanchart') {
      loadStanchartLogs().catch(e => console.error("Error reloading stanchart logs:", e));
    }
  };

  const applyLogsFilters = () => {
    if (activeTab === 'nira') {
      const query = buildNiraQuery(filters);
      loadNiraLogs().catch(e => console.error("Error Filtering NIRA logs:", e));
    } else if (activeTab === 'stanbic') {
      loadStanbicLogs().catch(e => console.error("Error Filtering Stanbic logs:", e));
    } else if (activeTab === 'stanchart') {
      loadStanchartLogs().catch(e => console.error("Error Filtering StanChart logs:", e));
    }
  };

  const resetLogsFilters = () => {
    onReloadLogs();
  };

  // UI utilities
  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleLogout = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();

    try {
      const logoutPromise = authService.logout();
      const timeoutPromise = new Promise((resolve) => setTimeout(resolve, 1000));

      localStorage.removeItem('accessToken');
      localStorage.removeItem('username');
      localStorage.removeItem('name');

      await Promise.race([logoutPromise, timeoutPromise]);
      window.location.href = '/login';
      window.location.reload();
    } catch (error) {
      console.error('Logout error:', error);
      window.location.href = '/login';
      window.location.reload();
    }
  };

  const renderYesNoDropdown = (integration, field, label) => {
    return (
        <div className="form-group">
          <label>{label}</label>
          <select
              value={configData[integration][field]}
              onChange={(e) => handleConfigChange(integration, field, e.target.value)}
          >
            <option value="YES">Yes</option>
            <option value="NO">No</option>
          </select>
        </div>
    );
  };

  // Loading state
  if (isVerifyingSession) {
    return (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Verifying session...</p>
        </div>
    );
  }

  if (!userData) {
    return null;
  }

  // Main render
  return (
      <div className={`dashboard-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <ToastContainer
            position="top-center"
            autoClose={3000}
            newestOnTop
            closeOnClick
            draggable
            pauseOnHover
            theme="light"
            style={{ zIndex: 9999 }}
        />

        {/* Sidebar */}
        <DashboardSidebar
            sidebarCollapsed={sidebarCollapsed}
            activeTab={activeTab}
            activeSubTab={activeSubTab}
            ppslogo={ppslogo}
            userName={authService.getLoggedInUserName()}
            onToggleSidebar={toggleSidebar}
            onSetActiveTab={setActiveTab}
            onSetActiveSubTab={setActiveSubTab}
            onLogout={handleLogout}
        />

        {/* Main Content */}
        <div className="main-content">
          {activeTab === 'dashboard' && (
              <div className="dashboard-overview">
                <h1>System Overview</h1>
                <div className="stats-container">
                  <div className="stat-card">
                    <h3>NIRA Verifications</h3>
                    <p className="stat-value">24</p>
                    <p className="stat-label">Today</p>
                  </div>
                  <div className="stat-card">
                    <h3>Stanbic Payments</h3>
                    <p className="stat-value">18</p>
                    <p className="stat-label">Processed</p>
                  </div>
                  <div className="stat-card">
                    <h3>StanChart Payments</h3>
                    <p className="stat-value">12</p>
                    <p className="stat-label">Processed</p>
                  </div>
                  <div className="stat-card">
                    <h3>Batch Records</h3>
                    <p className="stat-value">{batchData.length}</p>
                    <p className="stat-label">Active</p>
                  </div>
                </div>

                <div className="recent-activity">
                  <h2>Recent Activity</h2>
                  <div className="activity-list">
                    {/*{(niraPage.content || []).slice(0, 3).map((item, index) => {*/}
                    {/*    const statusText = item.status ? item.status.toLowerCase() : 'unknown';*/}
                    {/*    const isVerified = statusText === 'verified';*/}
                    {/*    return (*/}
                    {/*        <div className="activity-item" key={`nira-${index}`}>*/}
                    {/*            <div className={`activity-icon ${isVerified ? 'success' : 'warning'}`}>*/}
                    {/*                <i className={`fas ${isVerified ? 'fa-check' : 'fa-exclamation'}`}></i>*/}
                    {/*            </div>*/}
                    {/*            <div className="activity-details">*/}
                    {/*                <p>NIRA verification {statusText} for NIN {item.nin}</p>*/}
                    {/*                <small>{item.date}</small>*/}
                    {/*            </div>*/}
                    {/*        </div>*/}
                    {/*    );*/}
                    {/*})}*/}
                  </div>
                </div>
              </div>
          )}

          {/* Integration Sections */}
          {(activeTab === 'nira' || activeTab === 'stanbic' || activeTab === 'stanchart' || activeTab === 'batch') && (
              <div className="integration-section">
                <DashboardHeader activeTab={activeTab} />

                <IntegrationTabs
                    activeSubTab={activeSubTab}
                    onSetActiveSubTab={setActiveSubTab}
                />

                {/* Register View */}
                {activeSubTab === 'register' && (
                    <RegisterView
                        activeTab={activeTab}
                        filters={filters}
                        onFilterChange={handleFilterChange}
                        onApplyFilters={applyFilters}
                        onResetFilters={resetFilters}
                        niraPage={niraPage}
                        niraLoading={niraLoading}
                        niraError={niraError}
                        niraSize={niraSize}
                        onViewNiraRequest={handleViewNiraRequest}
                        onPageChange={loadNiraPage}
                        onPageSizeChange={(newSize) => {
                          setNiraSize(newSize);
                          loadNiraPage(0, newSize);
                        }}
                        // Stanbic props
                        stanbicData={stanbicData}
                        stanbicLoading={stanbicLoading}
                        stanbicError={stanbicError}
                        onViewStanbicBatch={handleViewStanbicBatch}
                        stanbicBreakdownData={stanbicBreakdownData}
                        stanbicBreakdownLoading={stanbicBreakdownLoading}
                        stanbicPaymentResponses={stanbicPaymentResponses}
                        selectedStanbicBatchId={selectedStanbicBatchId}
                        onCloseStanbicBreakdown={handleCloseStanbicBreakdown}
                        // Batch props
                        batchData={batchData}
                        batchLoading={batchLoading}
                        batchError={batchError}
                        onViewBatchBreakdown={handleViewBatchBreakdown}
                        batchBreakdownData={batchBreakdownData}
                        batchBreakdownLoading={batchBreakdownLoading}
                        selectedBatchId={selectedBatchId}
                        onCloseBreakdown={handleCloseBreakdown}
                    />
                )}

                {/* Configuration View */}
                {activeSubTab === 'config' && (
                    <ConfigForm
                        activeTab={activeTab}
                        isLoadingConfig={isLoadingConfig}
                        configData={configData}
                        onConfigChange={handleConfigChange}
                        onSaveConfig={saveConfig}
                        isSavingConfig={isSavingConfig}
                        onTestNiraIntegration={handleTestNiraIntegration}
                        isTestingNiraIntegration={isTestingNiraIntegration}
                        onResetNiraPassword={handleResetNiraPassword}
                        isResettingNiraPassword={isResettingNiraPassword}
                        onGenerateSSHKey={handleGenerateSSHKey}
                        isGeneratingStanbicSSHKey={isGeneratingStanbicSSHKey}
                        onGeneratePGPKey={handleGeneratePGPKey}
                        isGeneratingStanbicPGPKey={isGeneratingStanbicPGPKey}
                        onTestConnection={handleTestConnection}
                        isTestingConnection={isTestingConnection}
                        onReloadConfig={reloadConfig}
                        renderYesNoDropdown={renderYesNoDropdown}
                        // StanChart-specific props
                        onTestStanchartConnection={handleTestStanchartConnection}
                        isTestingStanchartConnection={isTestingStanchartConnection}
                        onActivateStanchartService={handleActivateStanchartService}
                        isActivatingStanchartService={isActivatingStanchartService}
                    />
                )}

                {activeSubTab === 'stats' && (
                    <StatsView
                        activeTab={activeTab}
                        onLoadStats={handleLoadStats}
                        isLoadingStats={isLoadingStats}
                        statusCounts={activeTab === 'nira' ? niraStatusCounts : []}
                        trendsByMonth={activeTab === 'nira' ? niraTrendsByMonth : []}
                    />
                )}

                {/* Logs View */}
                {activeSubTab === 'logs' && (
                    <LogsView
                        activeTab={activeTab}
                        filters={filters}
                        onFilterChange={handleFilterChange}
                        onApplyFilters={applyLogsFilters}
                        onResetFilters={resetLogsFilters}
                        onRefreshLogs={onReloadLogs}
                        isLoadingLogs={isLoadingLogs}
                        stanbicLogs={stanbicLogs}
                        niraLogs={niraLogs}
                        stanchartLogs={stanchartLogs}
                        expandedLogs={expandedLogs}
                        onToggleLogExpansion={toggleLogExpansion}
                    />
                )}
              </div>
          )}

          {/* OAuth Clients Section */}
          {activeTab === 'oauth-clients' && (
              <div className="integration-section">
                <DashboardHeader activeTab={activeTab} />

                <OauthClientsView
                    clients={oauthClients}
                    isLoading={isLoadingOAuthClients}
                    isCreating={isCreatingOAuthClient}
                    isRegenerating={isRegeneratingOAuthClient}
                    isDeleting={isDeletingOAuthClient}
                    onCreateClient={handleCreateOAuthClient}
                    onRegenerateCredentials={handleRegenerateOAuthCredentials}
                    onDeleteClient={handleDeleteOAuthClient}
                    onRefresh={loadOAuthClients}
                />
              </div>
          )}

          {/* SMTP Setup Section */}
          {activeTab === 'smtp-setup' && (
              <div className="integration-section">
                <DashboardHeader activeTab={activeTab} />

                <SmtpSetupView
                    notificationEnums={{
                      yesNoOptions: [
                        { id: 'YES', name: 'Yes' },
                        { id: 'NO', name: 'No' }
                      ]
                    }}
                />
              </div>
          )}

          {/* Notification Templates Section */}
          {activeTab === 'notification-templates' && (
              <div className="integration-section">
                <DashboardHeader activeTab={activeTab} />

                <NotificationTemplatesView
                    notificationEnums={{
                      platforms: notificationPlatforms,
                      categories: notificationCategories,
                      templateApplications: notificationTemplateApplications,
                      notificationTypes: notificationTypes,
                      reservedWords: reservedWords
                    }}
                    smtpSetups={smtpSetups}
                    loading={isLoadingNotificationData}
                />
              </div>
          )}
        </div>
      </div>
  );
};

export default Dashboard;